import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Breadcrumb, Modal, Radio, Spin, Rate, message } from "antd";
import axios from "axios";
import { BASE_URL } from "../../../Constant";
import { getSingleOrderApi } from "../../../Features/Order/Order";
import { addReviewApi, getSingleReviewApi } from "../../../Features/Setting/Setting";
import { getProfileApi } from "../../../Features/User/User";
import { getOrderVariantTags } from "../../../utils/cartVariants";
import styles from "./index.module.scss";

const GOLD = "#C5A028";

const formatPrice = (p) => Number(p || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const statusClass = (s) => {
  const map = { Pending: "statusPending", Confirmed: "statusConfirmed", Dispatched: "statusDispatched", Delivered: "statusDelivered", Cancelled: "statusCancelled" };
  return styles[map[s]] || styles.statusDefault || "";
};

export default function OrderView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const userToken = useSelector((s) => s.user?.token);
  const orderData = useSelector((s) => s.order?.orderDeatils);
  const reviewData = useSelector((s) => s.setting?.singleReviewData);
  const reviewLoading = useSelector((s) => s.setting?.settingLoading);

  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [viewReviewOpen, setViewReviewOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (state?._id && userToken) {
      dispatch(getSingleOrderApi(state._id, userToken));
      dispatch(getSingleReviewApi(state._id, userToken));
      dispatch(getProfileApi(userToken));
    }
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  const status = orderData?.Status || state?.Status || "Pending";
  const isCancelled = status === "Cancelled" || orderData?.OrderType === "6";
  const address = orderData?.Address || {};
  const cartItems = orderData?.cartData || [];
  const total = orderData?.FinalPrice || 0;
  const ratingLabels = ["Very Poor", "Poor", "Okay", "Good", "Excellent"];

  // 24-hour cancellation window
  const [timeLeft, setTimeLeft] = useState("");
  const orderCreated = orderData?.createdAt || state?.createdAt;
  const cancelDeadline = orderCreated ? new Date(new Date(orderCreated).getTime() + 24 * 60 * 60 * 1000) : null;
  const canCancel = cancelDeadline ? new Date() < cancelDeadline : false;

  useEffect(() => {
    if (!cancelDeadline || isCancelled) return;
    const tick = () => {
      const now = new Date();
      const diff = cancelDeadline - now;
      if (diff <= 0) { setTimeLeft(""); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [cancelDeadline, isCancelled]); // eslint-disable-line

  const handleCancel = async () => {
    if (!cancelReason) { message.warning("Please select a reason"); return; }
    try {
      await axios.put(`${BASE_URL}/order/update/type/${state?._id}`, {
        order_status: "Cancelled",
        reason: cancelReason,
      }, { headers: { Authorization: userToken } });
      setCancelOpen(false);
      message.success("Order cancelled");
      navigate("/my-account", { state: { tab: "orders" } });
    } catch (err) {
      message.error("Failed to cancel order");
    }
  };

  const handleReview = () => {
    if (!rating) { message.warning("Please select a rating"); return; }
    dispatch(addReviewApi({ order: state?._id, comment, text: ratingLabels[rating - 1], rating: String(rating) }, () => {
      setReviewOpen(false);
      dispatch(getSingleReviewApi(state._id, userToken));
      message.success("Review submitted");
    }, userToken));
  };

  if (loading && !orderData) {
    return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}><Spin size="large" /></div>;
  }

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbBar}>
        <div className={styles.breadcrumbInner}>
          <Breadcrumb className={styles.breadcrumb} items={[
            { title: <Link to="/">Home</Link> },
            { title: <Link to="/my-account" state={{ tab: "orders" }}>My Orders</Link> },
            { title: orderData?.orderId || "Order" },
          ]} />
        </div>
      </div>

      {/* Page Title */}
      <div className={styles.pageTitle}>
        <span className={styles.titleEyebrow}>The Diamond Counter</span>
        <h1 className={styles.titleText}>Order Details</h1>
        <div className={styles.titleLine} />
      </div>

      <div className={styles.container}>
        {/* Order Header */}
        <div className={styles.orderHeader}>
          <div>
            <h2 className={styles.orderId}>{orderData?.orderId || state?.orderId || "Order"}</h2>
            <p className={styles.orderDate}>Placed on {formatDate(orderData?.createdAt || state?.createdAt)}</p>
          </div>
          <span className={`${styles.statusBadge} ${statusClass(status)}`}>{isCancelled ? "Cancelled" : status}</span>
        </div>

        <div className={styles.detailLayout}>
          {/* Left — Items */}
          <div>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Items ({cartItems.length})</h3>
              <div className={styles.itemsList}>
                {cartItems.map((item, i) => {
                  const tags = getOrderVariantTags(item);

                  return (
                    <div key={i} className={styles.item}>
                      <div className={styles.itemImg}>
                        {item?.productImage && <img src={item.productImage} alt="" />}
                      </div>
                      <div className={styles.itemBody}>
                        <div className={styles.itemHeader}>
                          <p className={styles.itemName}>{item?.productName || "Product"}</p>
                          <span className={styles.itemPrice}>£{formatPrice(item?.totalPrice)}</span>
                        </div>
                        {tags.length > 0 && (
                          <div className={styles.itemTags}>
                            {tags.map(([label, value], j) => (
                              <span key={j} className={styles.itemTag}><span className={styles.itemTagLabel}>{label}:</span> {value}</span>
                            ))}
                          </div>
                        )}
                        <div className={styles.itemFooter}>
                          <span>Qty: {item?.quantity}</span>
                          <span className={styles.itemUnit}>£{formatPrice(item?.unitPrice)} each</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Address */}
            {address?.Name && (
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Delivery Address</h3>
                <div className={styles.addressBlock}>
                  <div className={styles.addressTop}>
                    {address.Type && <span className={styles.addressBadge}>{address.Type}</span>}
                    <span className={styles.addressName}>{address.Name}</span>
                    {address.Phone_Number && <span className={styles.addressPhone}>{address.Phone_Number}</span>}
                  </div>
                  <p className={styles.addressText}>
                    {[address.House, address.Full_Address, address.City, address.State].filter(Boolean).join(", ")}
                    {address.Pincode && ` - ${address.Pincode}`}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right — Summary */}
          <div>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Payment Summary</h3>
              <div className={styles.summaryRows}>
                <div className={styles.summaryRow}><span>Subtotal</span><span>£{formatPrice(orderData?.OriginalPrice)}</span></div>
                <div className={styles.summaryRow}><span>Shipping</span><span>{Number(orderData?.Shipping_Charge) > 0 ? `£${formatPrice(orderData?.Shipping_Charge)}` : "Free"}</span></div>
                {Number(orderData?.CouponPrice) > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryDiscount}`}><span>Coupon</span><span>-£{formatPrice(orderData?.CouponPrice)}</span></div>
                )}
                {Number(orderData?.VAT_Amount) > 0 && (
                  <div className={styles.summaryRow}><span>VAT ({orderData?.VAT_Rate || 20}%)</span><span>£{formatPrice(orderData?.VAT_Amount)}</span></div>
                )}
                <div className={styles.summaryDivider} />
                <div className={`${styles.summaryRow} ${styles.summaryTotal}`}><span>Total{Number(orderData?.VAT_Amount) > 0 ? " (inc. VAT)" : ""}</span><span>£{formatPrice(total)}</span></div>
              </div>
              {orderData?.PaymentStatus && (
                <div className={styles.paymentRow}><span>Payment Status</span><span>{orderData.PaymentStatus}</span></div>
              )}
              {orderData?.PaymentType && (
                <div className={styles.paymentRow}><span>Payment Method</span><span>{orderData.PaymentType}</span></div>
              )}
            </div>

            {/* Actions */}
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>Actions</h3>
              <div className={styles.actions}>
                {status === "Delivered" && (
                  reviewData?.length > 0 ? (
                    <button className={styles.actionBtn} onClick={() => setViewReviewOpen(true)}>View Your Review</button>
                  ) : (
                    <button className={styles.actionBtnPrimary} onClick={() => setReviewOpen(true)}>Leave a Review</button>
                  )
                )}
                {!isCancelled && status !== "Delivered" && (
                  canCancel ? (
                    <div>
                      <button className={styles.actionBtnDanger} onClick={() => setCancelOpen(true)}>Cancel Order</button>
                      {timeLeft && <p style={{ fontSize: 11, color: "#e74c3c", marginTop: 6, textAlign: "center" }}>Cancellation window closes in {timeLeft}</p>}
                    </div>
                  ) : (
                    <div>
                      <button className={styles.actionBtnDisabled} disabled>Cancel Order</button>
                      <p style={{ fontSize: 11, color: "#999", marginTop: 6, textAlign: "center" }}>Cancellation window has expired (24 hours)</p>
                    </div>
                  )
                )}
                {isCancelled && orderData?.reason && (
                  <div style={{ padding: "12px 16px", background: "#fef2f2", border: "1px solid #fecaca", fontSize: 12, color: "#991b1b" }}>
                    <strong>Cancellation reason:</strong> {orderData.reason}
                  </div>
                )}
                <button className={styles.actionBtn} onClick={() => navigate("/my-account", { state: { tab: "orders" } })}>Back to My Orders</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <Modal open={cancelOpen} onCancel={() => setCancelOpen(false)} footer={null} centered title={null} width={480} className={styles.themedModal}>
        <h3 className={styles.modalTitle}>Cancel Order</h3>
        <p style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>Please select a reason for cancellation:</p>
        <Radio.Group value={cancelReason} onChange={e => setCancelReason(e.target.value)} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {["Product not required any more", "Purchased product from somewhere else", "Incorrect size / colour / type ordered", "Other"].map(r => (
            <Radio key={r} value={r} style={{ fontSize: 13 }}>{r}</Radio>
          ))}
        </Radio.Group>
        <button className={styles.actionBtnDanger} style={{ width: "100%", marginTop: 20 }} onClick={handleCancel}>Confirm Cancellation</button>
      </Modal>

      {/* Review Modal */}
      <Modal open={reviewOpen} onCancel={() => setReviewOpen(false)} footer={null} centered title={null} width={480} className={styles.themedModal}>
        <h3 className={styles.modalTitle}>Leave a Review</h3>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Rate value={rating} onChange={setRating} style={{ fontSize: 28, color: GOLD }} />
          {rating > 0 && <p style={{ fontSize: 13, color: GOLD, marginTop: 6, fontWeight: 600 }}>{ratingLabels[rating - 1]}</p>}
        </div>
        <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Share your experience..." rows={4}
          style={{ width: "100%", border: "1px solid #e0e0e0", padding: "10px 14px", fontSize: 13, fontFamily: "Poppins, sans-serif", resize: "vertical", outline: "none", boxSizing: "border-box" }} />
        <button className={styles.actionBtnPrimary} style={{ width: "100%", marginTop: 14 }} onClick={handleReview} disabled={reviewLoading}>
          {reviewLoading ? "Submitting..." : "Submit Review"}
        </button>
      </Modal>

      {/* View Review Modal */}
      <Modal open={viewReviewOpen} onCancel={() => setViewReviewOpen(false)} footer={null} centered title={null} width={480} className={styles.themedModal}>
        <h3 className={styles.modalTitle}>Your Review</h3>
        {reviewData?.[0] && (
          <div style={{ textAlign: "center" }}>
            <Rate value={Number(reviewData[0].rating)} disabled style={{ fontSize: 28, color: GOLD }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: "#111", marginTop: 8 }}>{reviewData[0].text}</p>
            {reviewData[0].comment && <p style={{ fontSize: 13, color: "#666", marginTop: 6, lineHeight: 1.6 }}>{reviewData[0].comment}</p>}
          </div>
        )}
      </Modal>
    </div>
  );
}
